import { createSelector } from 'reselect';
import { pageType } from 'utils/tracking';
import { getArticleSlug, getContentType, getPathType, getUrl, getQuery } from 'selectors/getMatch';
import getLocale from 'selectors/getLocale';
import { selectUUID, selectIsInitialized } from 'selectors/user';
import { createGetContentItemBySlug } from 'selectors/createGetContentItem';
import { CONTENT_ITEM_SINGLE } from 'config/types/path';
import getLearnLanguage from 'selectors/getLearnLanguage';
import getIsInProductMagazine from 'selectors/getIsInProductMagazine';

/**
 * Selector that will return the payload for the event tracking.
 *
 * @param {string} state of all
 * @param {string} uuid of the user
 * @param {string} isUserLoading check if the request is done
 * @param {string} pathType type of the page
 * @param {string} locale Magazine language edition
 * @param {string} url path
 * @param {string} query query params for the url
 * @param {string} contentType data of the article
 * @param {string} articleSlug slug of the article
 * @return {function}   selector function
 */
const getPageTrackingData = createSelector(
  [
    (state) => state,
    selectUUID,
    selectIsInitialized,
    getPathType,
    getLocale,
    getUrl,
    getQuery,
    getContentType,
    getArticleSlug,
    getLearnLanguage,
    getIsInProductMagazine,
  ],
  (
    state,
    uuid = null,
    isUserInitialized,
    pathType,
    locale,
    url,
    query,
    contentType,
    articleSlug,
    learnLanguage = null,
    isInProductMagazine = null,
  ) => {
    let articlePayload = {
      article_slug: null,
      article_author: null,
      article_category: null,
      article_tags: null,
    };
    const search = Object.entries(query)
      .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
      .join('&');

    if (!isUserInitialized) {
      return { isLoading: true, data: {} };
    }

    if (pathType === CONTENT_ITEM_SINGLE) {
      const getItem = createGetContentItemBySlug(contentType);
      const item = getItem(state);
      if (!item) {
        return { isLoading: true, data: {} };
      }
      const {
        metadata: { categories: [category] = [], coauthors: [author] = [], tags },
      } = item;
      articlePayload = {
        ...articlePayload,
        ...(author?.slug && { article_author: author.slug }),
        ...(category && { article_category: category }),
        ...(tags?.length && { article_tags: tags.map(({ label }) => label) }),
        article_slug: articleSlug,
      };
    }

    return {
      data: {
        ...articlePayload,
        page_type: pageType[pathType],
        page_path: `${url}${search.length ? `?${search}` : ''}`,
        locale: 'en-US' === locale ? 'en' : locale,
        ...(uuid && { uuid }),
        learn_language_alpha3: learnLanguage,
        in_product: isInProductMagazine,
      },
      isLoading: false,
    };
  },
);

export default getPageTrackingData;
